import DateFnsUtils from '@date-io/date-fns';
import { toDate } from 'date-fns-tz';
import { TextFieldProps } from '@material-ui/core/TextField';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import PropTypes from 'prop-types';
import { FieldTitle, InputProps, useInput, InputHelperText } from 'react-admin';
import React from 'react';
import sanitizeRestProps from './sanitizeRestProps';
import { isEmpty } from 'lodash';

/**
 * Convert Date object to String
 *
 * @param {Date} value value to convert
 * @param source
 * @param utcMode
 * @param source
 * @param utcMode
 * @returns {String} A standardized date (yyyy-MM-dd), to be passed to an <input type="date" />
 */
const convertDateToString = (value: Date, source, utcMode) => {
  if (!(value instanceof Date) || isNaN(value.getDate())) return '';
  const pad = '00';
  const yyyy = utcMode
    ? value.getUTCFullYear().toString()
    : value.getFullYear().toString();
  const MM = utcMode
    ? (value.getUTCMonth() + 1).toString()
    : (value.getMonth() + 1).toString();
  const dd = utcMode
    ? value.getUTCDate().toString()
    : value.getDate().toString();

  return `${yyyy}-${(pad + MM).slice(-2)}-${(pad + dd).slice(-2)}`;
};

const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

const getStringFromDate = (value: string | Date, source, utcMode) => {
  // null, undefined and empty string values should not go through dateFormatter
  // otherwise, it returns undefined and will make the input an uncontrolled one.
  if (value == null || value === '') {
    return '';
  }

  if (value instanceof Date) {
    return convertDateToString(value, source, utcMode);
  }

  // valid dates should not be converted
  if (dateRegex.test(value)) {
    return value;
  }

  return convertDateToString(new Date(value), source, utcMode);
};

export class LocalizedUtils extends DateFnsUtils {
  date(value) {
    if (typeof value === 'undefined') {
      return new Date();
    }
    if (value === null) {
      return null;
    }
    //new Date(value) in combination with timezone can increase or decrease day by one
    //toDate(value) from date-fns-tz just creates Date object with given day from string
    if (typeof value === 'string') {
      return toDate(value);
    }
    return new Date(value);
  }
}

export const DateInput = ({
  label,
  customParseValue,
  customOnChange,
  options,
  source,
  resource,
  helperText,
  margin = 'dense',
  utcMode,
  meta,
  onBlur,
  onFocus,
  parse,
  validate,
  format = (value, source) => getStringFromDate(value, source, utcMode),
  variant = 'standard',
  formatCustom = 'MM/dd/yyyy',
  showFieldLabel = true,
  ...rest
}: DateInputProps) => {
  const {
    id,
    input,
    isRequired,
    meta: { error, touched }
  } = useInput({
    format,
    onBlur,
    onFocus,
    parse,
    resource,
    source,
    validate,
    ...rest
  });
  const onChange = value => {
    let parsedValue = value;
    if (customParseValue) {
      parsedValue = customParseValue(parsedValue);
    }
    input.onChange(parsedValue);
    input.onBlur(parsedValue);
    if (typeof customOnChange === 'function') {
      customOnChange(value);
    }
  };
  return (
    <MuiPickersUtilsProvider utils={LocalizedUtils}>
      <KeyboardDatePicker
        id={id}
        {...input}
        disableToolbar={true}
        variant={variant}
        format={formatCustom}
        margin={margin}
        label={
          showFieldLabel && (
            <FieldTitle
              label={label}
              source={source}
              resource={resource}
              isRequired={isRequired}
            />
          )
        }
        error={!!(touched && error)}
        onChange={onChange}
        value={input.value ? input.value : null}
        helperText={
          (isEmpty(error) ? false : error) || helperText
            ? React.createElement(InputHelperText, {
                touched: touched,
                error: error,
                helperText: helperText
              })
            : null
        }
        KeyboardButtonProps={{
          id: 'change-date',
          'aria-label': 'change date'
        }}
        InputProps={{
          autoComplete: 'off'
        }}
        autoOk={true}
        {...sanitizeRestProps(rest)}
        DialogProps={{
          id: 'start-date-button'
        }}
        rightArrowButtonProps={{
          id: 'right-arrow'
        }}
        leftArrowButtonProps={{
          id: 'left-arrow'
        }}
      />
    </MuiPickersUtilsProvider>
  );
};

DateInput.propTypes = {
  label: PropTypes.string,
  options: PropTypes.object,
  resource: PropTypes.string,
  source: PropTypes.string
};

DateInput.defaultProps = {
  options: {}
};

export type DateInputProps = InputProps<TextFieldProps> &
  Omit<TextFieldProps, 'helperText' | 'label'>;

export default DateInput;
